import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import { LanguageContext } from '../contexts/LanguageContext';

const Hero = () => {
  const { translations } = useContext(LanguageContext);
  
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true
    });
  }, []);
  
  return (
    <section className="relative overflow-hidden bg-white dark:bg-gray-900 text-blue-900 dark:text-blue-50 py-16 md:py-20">
      {/* Background with decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-blue-50 dark:bg-blue-950 opacity-5"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto relative">
          {/* Decorative elements - simple blue borders */}
          <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-blue-300 -translate-x-10 -translate-y-10"></div>
          <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-blue-300 translate-x-10 translate-y-10"></div>
          
          <div data-aos="fade-up" data-aos-duration="1200" className="text-center md:text-left">
            <div className="inline-flex items-center py-2 px-4 rounded-md bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 mb-8 border border-blue-200 dark:border-blue-800">
              <svg className="w-4 h-4 mr-2 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              <span className="uppercase text-blue-900 dark:text-blue-100 font-medium">{translations['hero.securityBadge'] || 'Enterprise-Grade Security'}</span>
            </div>
            
            <div className="mb-6">
              <h1 className="text-center md:text-left">
                <span className="block mb-3 text-4xl sm:text-5xl md:text-6xl font-bold text-blue-900 dark:text-blue-100">
                  {translations['hero.title'] ? translations['hero.title'].split(' ')[0] : 'Fortifying'}
                </span>
                <span className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-blue-900 dark:text-blue-100">
                  {translations['hero.title'] ? translations['hero.title'].split(' ').slice(1).join(' ') : 'Modern Infrastructure'}
                </span>
              </h1>
            </div>
            
            <p className="text-lg md:text-xl lg:text-2xl text-blue-900 dark:text-blue-100 font-medium mb-6 text-center md:text-left max-w-3xl mx-auto md:mx-0">
              {translations['hero.subtitle'] || 'Enterprise-grade Kubernetes solutions & comprehensive risk consulting'}
            </p>
            <p className="text-lg md:text-xl text-blue-900 dark:text-blue-100 font-medium mb-6 text-center md:text-left max-w-3xl mx-auto md:mx-0">
              <span className="font-medium text-blue-900 dark:text-blue-100">
                {translations['hero.expertise'] || 'Fepsec delivers dual expertise in both technology and security.'}
              </span> 
              {translations['hero.expertiseDescription'] || 'Our solutions bridge Kubernetes infrastructure with physical security, electronics security, fire & life safety, and cyber security consulting—offering a unified approach to protecting your digital and physical assets.'}
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-800 text-white font-bold rounded-lg shadow-lg transform transition-transform hover:scale-105 text-lg">
                {translations['hero.primaryCta'] || 'Get Started'}
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </Link>
              <Link to="/platform-engineering" className="inline-flex items-center justify-center px-6 py-3 bg-blue-50 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-gray-700 text-blue-800 dark:text-blue-200 font-bold rounded-lg shadow-md transform transition-transform hover:scale-105 text-lg border border-blue-200 dark:border-gray-700 mr-2">
                {translations['hero.kubernetesCta'] || 'Kubernetes Services'}
              </Link>
              <Link to="/physical-security" className="inline-flex items-center justify-center px-6 py-3 bg-blue-50 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-gray-700 text-blue-800 dark:text-blue-200 font-bold rounded-lg shadow-md transform transition-transform hover:scale-105 text-lg border border-blue-200 dark:border-gray-700">
                {translations['hero.riskCta'] || 'Risk Consulting'}
              </Link>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center mt-6 gap-2 mx-auto px-2 md:px-0 max-w-3xl">
            {/* OpenShift Card */}
            <div className="flex-1 min-w-[120px] max-w-[150px] h-[100px] bg-blue-50 dark:bg-gray-800 py-2 px-3 rounded-md border border-blue-200 dark:border-gray-700 shadow-sm hover:shadow transition-all duration-300 flex flex-col items-center justify-center space-y-2">
              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-gray-700 border border-blue-200 dark:border-gray-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-700 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h3 className="text-sm font-bold text-blue-800 dark:text-blue-300 text-center">{translations['services.openshift.title'] || 'OpenShift Managé'}</h3>
            </div>
            
            {/* Uptime Card */}
            <div className="flex-1 min-w-[120px] max-w-[150px] h-[100px] bg-blue-50 dark:bg-gray-800 py-2 px-3 rounded-md border border-blue-200 dark:border-gray-700 shadow-sm hover:shadow transition-all duration-300 flex flex-col items-center justify-center space-y-2">
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">99</span>
                <span className="text-lg font-bold text-blue-400 dark:text-blue-500">%</span>
              </div>
              <div className="text-center text-xs font-medium uppercase tracking-wider text-blue-800 dark:text-blue-300">
                {translations['hero.stats.uptime'] || 'Platform Uptime'}
              </div>
            </div>
            
            {/* Clusters Card */}
            <div className="flex-1 min-w-[120px] max-w-[150px] h-[100px] bg-blue-50 dark:bg-gray-800 py-2 px-3 rounded-md border border-blue-200 dark:border-gray-700 shadow-sm hover:shadow transition-all duration-300 flex flex-col items-center justify-center space-y-2">
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">130</span>
                <span className="text-lg font-bold text-blue-400 dark:text-blue-500">+</span>
              </div>
              <div className="text-center text-xs font-medium uppercase tracking-wider text-blue-800 dark:text-blue-300">
                {translations['hero.stats.clusters'] || 'Kubernetes Clusters'}
              </div>
            </div>
            
            {/* Security Card */}
            <div className="flex-1 min-w-[120px] max-w-[150px] h-[100px] bg-blue-50 dark:bg-gray-800 py-2 px-3 rounded-md border border-blue-200 dark:border-gray-700 shadow-sm hover:shadow transition-all duration-300 flex flex-col items-center justify-center space-y-2">
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">24</span>
                <span className="text-lg font-bold text-blue-400 dark:text-blue-500">/7</span>
              </div>
              <div className="text-center text-xs font-medium uppercase tracking-wider text-blue-800 dark:text-blue-300">
                {translations['hero.stats.security'] || 'Security Monitoring'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Simple blue border at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-300"></div>
    </section>
  );
};

export default Hero;